import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  format, parse, isValid, isSameDay, isToday, startOfMonth, addDays, addMonths,
  addYears, setMonth, setYear, isSameMonth,
} from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, AlertTriangle } from 'lucide-react';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DISPLAY_FMT = 'dd/MM/yyyy';

const cell = 'h-9 w-9 rounded-md text-sm flex items-center justify-center transition-colors select-none';

// Parse an unambiguous DD/MM/YYYY (also tolerate - or . separators)
const parseTyped = (str) => {
  const m = str.trim().match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (!m) return null;
  const d = parse(`${m[1].padStart(2, '0')}/${m[2].padStart(2, '0')}/${m[3]}`, DISPLAY_FMT, new Date());
  return isValid(d) ? d : null;
};

const DatePickerField = ({ value, onChange, placeholder = 'Select date', warnIfPast = false, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('day'); // day | month | year
  const [viewDate, setViewDate] = useState(value || new Date());
  const [focused, setFocused] = useState(value || new Date());
  const [text, setText] = useState(value ? format(value, DISPLAY_FMT) : '');
  const [typeError, setTypeError] = useState('');
  const gridRef = useRef(null);

  // Keep the text field in sync when the external value changes
  useEffect(() => {
    setText(value ? format(value, DISPLAY_FMT) : '');
  }, [value]);

  // When opening, reset to the selected date (or today) without mutating the value
  useEffect(() => {
    if (open) {
      const base = value || new Date();
      setViewDate(base);
      setFocused(base);
      setView('day');
      setTimeout(() => gridRef.current?.focus(), 0);
    }
  }, [open, value]);

  const today = new Date();
  const isPast = value && !isToday(value) && value < new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const days = useMemo(() => {
    const monthStart = startOfMonth(viewDate);
    const gridStart = addDays(monthStart, -monthStart.getDay());
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [viewDate]);

  const commit = (d) => {
    onChange(d);
    setText(format(d, DISPLAY_FMT));
    setTypeError('');
    setOpen(false);
  };

  const onType = (e) => {
    const v = e.target.value;
    setText(v);
    if (v.trim() === '') { onChange(undefined); setTypeError(''); return; }
    const parsed = parseTyped(v);
    if (parsed) { onChange(parsed); setTypeError(''); }
  };
  const onTypeBlur = () => {
    if (text.trim() === '') { setTypeError(''); return; }
    if (!parseTyped(text)) setTypeError('Enter a valid date (DD/MM/YYYY)');
  };

  const onKeyDown = (e) => {
    if (view !== 'day') return;
    let next = null;
    if (e.key === 'ArrowLeft') next = addDays(focused, -1);
    else if (e.key === 'ArrowRight') next = addDays(focused, 1);
    else if (e.key === 'ArrowUp') next = addDays(focused, -7);
    else if (e.key === 'ArrowDown') next = addDays(focused, 7);
    else if (e.key === 'Enter') { e.preventDefault(); commit(focused); return; }
    else return;
    e.preventDefault();
    setFocused(next);
    if (!isSameMonth(next, viewDate)) setViewDate(next);
  };

  const yearBlockStart = Math.floor(viewDate.getFullYear() / 16) * 16;

  return (
    <div className={className}>
      <div className="relative mt-1.5">
        <Input
          value={text}
          onChange={onType}
          onBlur={onTypeBlur}
          onKeyDown={(e) => { if (e.key === 'Enter') { const p = parseTyped(text); if (p) commit(p); } }}
          placeholder={placeholder}
          className="h-11 pr-11"
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" aria-label="Open calendar"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
              <CalendarIcon size={17} />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-3">
            <div ref={gridRef} tabIndex={0} onKeyDown={onKeyDown} className="outline-none">
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <button type="button" onClick={() => {
                  if (view === 'day') setViewDate(addMonths(viewDate, -1));
                  else if (view === 'month') setViewDate(addYears(viewDate, -1));
                  else setViewDate(addYears(viewDate, -16));
                }} className="h-7 w-7 rounded-md border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors">
                  <ChevronLeft size={16} />
                </button>

                <button type="button" onClick={() => setView(view === 'day' ? 'month' : view === 'month' ? 'year' : 'day')}
                  className="px-3 h-7 rounded-md text-sm font-semibold text-slate-800 hover:bg-slate-100 transition-colors">
                  {view === 'day' && format(viewDate, 'MMMM yyyy')}
                  {view === 'month' && format(viewDate, 'yyyy')}
                  {view === 'year' && `${yearBlockStart} – ${yearBlockStart + 15}`}
                </button>

                <button type="button" onClick={() => {
                  if (view === 'day') setViewDate(addMonths(viewDate, 1));
                  else if (view === 'month') setViewDate(addYears(viewDate, 1));
                  else setViewDate(addYears(viewDate, 16));
                }} className="h-7 w-7 rounded-md border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors">
                  <ChevronRight size={16} />
                </button>
              </div>

              {/* Day view */}
              {view === 'day' && (
                <>
                  <div className="grid grid-cols-7 mb-1">
                    {WEEKDAYS.map((w) => <div key={w} className="h-8 w-9 flex items-center justify-center text-xs font-medium text-slate-400">{w}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-y-0.5">
                    {days.map((d) => {
                      const selected = value && isSameDay(d, value);
                      const isFocus = isSameDay(d, focused);
                      const outside = !isSameMonth(d, viewDate);
                      const td = isToday(d);
                      let cls = 'text-slate-700 hover:bg-slate-100';
                      if (outside) cls = 'text-slate-300 hover:bg-slate-50';
                      if (td && !selected) cls = 'border border-blue-500 text-blue-600 font-semibold hover:bg-blue-50';
                      if (selected) cls = 'bg-blue-600 text-white font-semibold hover:bg-blue-600';
                      return (
                        <button type="button" key={d.toISOString()} tabIndex={-1}
                          onClick={() => commit(d)}
                          onMouseEnter={() => setFocused(d)}
                          className={`${cell} ${cls} ${isFocus && !selected ? 'ring-2 ring-blue-400' : ''}`}>
                          {d.getDate()}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Month view */}
              {view === 'month' && (
                <div className="grid grid-cols-3 gap-2 w-[252px]">
                  {MONTHS.map((m, idx) => {
                    const isSel = value && value.getMonth() === idx && value.getFullYear() === viewDate.getFullYear();
                    const isCur = today.getMonth() === idx && today.getFullYear() === viewDate.getFullYear();
                    let cls = 'text-slate-700 hover:bg-slate-100';
                    if (isCur && !isSel) cls = 'border border-blue-500 text-blue-600 font-semibold hover:bg-blue-50';
                    if (isSel) cls = 'bg-blue-600 text-white font-semibold';
                    return (
                      <button type="button" key={m} onClick={() => { setViewDate(setMonth(viewDate, idx)); setView('day'); }}
                        className={`h-10 rounded-md text-sm transition-colors ${cls}`}>{m}</button>
                    );
                  })}
                </div>
              )}

              {/* Year view */}
              {view === 'year' && (
                <div className="grid grid-cols-4 gap-2 w-[252px]">
                  {Array.from({ length: 16 }, (_, i) => yearBlockStart + i).map((y) => {
                    const isSel = value && value.getFullYear() === y;
                    const isCur = today.getFullYear() === y;
                    let cls = 'text-slate-700 hover:bg-slate-100';
                    if (isCur && !isSel) cls = 'border border-blue-500 text-blue-600 font-semibold hover:bg-blue-50';
                    if (isSel) cls = 'bg-blue-600 text-white font-semibold';
                    return (
                      <button type="button" key={y} onClick={() => { setViewDate(setYear(viewDate, y)); setView('month'); }}
                        className={`h-10 rounded-md text-sm transition-colors ${cls}`}>{y}</button>
                    );
                  })}
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {typeError && <p className="text-red-500 text-xs mt-1">{typeError}</p>}
      {!typeError && warnIfPast && isPast && (
        <p className="text-amber-600 text-xs mt-1 flex items-center gap-1"><AlertTriangle size={12} /> This date has already passed</p>
      )}
    </div>
  );
};

export default DatePickerField;
